/**
 * Update Required Modal
 * 
 * Shown when a new version of the app has been deployed and the user
 * needs to refresh their browser. Offers to save work before refreshing.
 */

import React, { useState } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Typography,
  Box,
  CircularProgress,
} from '@mui/material';
import RefreshIcon from '@mui/icons-material/Refresh';
import SaveIcon from '@mui/icons-material/Save';
import NewReleasesIcon from '@mui/icons-material/NewReleases';

interface UpdateRequiredModalProps {
  open: boolean;
  onSaveAndRefresh: () => Promise<void>;
  onRefreshOnly: () => void;
}

export const UpdateRequiredModal: React.FC<UpdateRequiredModalProps> = ({
  open,
  onSaveAndRefresh,
  onRefreshOnly,
}) => {
  const [saving, setSaving] = useState(false);
  const [saveError, setSaveError] = useState<string | null>(null);

  const handleSaveAndRefresh = async () => {
    setSaving(true);
    setSaveError(null);
    try {
      await onSaveAndRefresh();
      // Refresh will happen in the callback
    } catch (error: any) {
      console.error('[UpdateRequiredModal] Save failed:', error);
      setSaveError(error.message || 'Failed to save. You can try again or refresh without saving.');
      setSaving(false);
    }
  };

  return (
    <Dialog
      open={open}
      maxWidth="sm"
      fullWidth
      PaperProps={{
        sx: {
          bgcolor: '#1a1a2e',
          color: '#fff',
          borderRadius: 2,
        },
      }}
    >
      <DialogTitle sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
        <NewReleasesIcon sx={{ color: '#4CAF50', fontSize: 28 }} />
        <Typography variant="h6" component="span">
          Update Available
        </Typography>
      </DialogTitle>
      
      <DialogContent>
        <Typography sx={{ mb: 2, color: '#b0b0c0' }}>
          A new version of Creative NodeFlow has been released. Please refresh your browser to continue using the app.
        </Typography>
        
        <Box
          sx={{
            bgcolor: 'rgba(255, 193, 7, 0.1)',
            border: '1px solid rgba(255, 193, 7, 0.3)',
            borderRadius: 1,
            p: 2,
            mb: 2,
          }}
        >
          <Typography variant="body2" sx={{ color: '#ffc107' }}>
            💡 We recommend saving your work before refreshing to ensure nothing is lost.
          </Typography>
        </Box>

        {saveError && (
          <Box
            sx={{
              bgcolor: 'rgba(244, 67, 54, 0.1)',
              border: '1px solid rgba(244, 67, 54, 0.3)',
              borderRadius: 1,
              p: 2,
              mb: 2,
            }}
          >
            <Typography variant="body2" sx={{ color: '#f44336' }}>
              {saveError}
            </Typography>
          </Box>
        )}
      </DialogContent>

      <DialogActions sx={{ p: 2, pt: 0, gap: 1 }}>
        <Button
          onClick={onRefreshOnly}
          variant="outlined"
          startIcon={<RefreshIcon />}
          disabled={saving}
          sx={{
            color: '#b0b0c0',
            borderColor: '#4a4a5a',
            '&:hover': {
              borderColor: '#6a6a7a',
              bgcolor: 'rgba(255,255,255,0.05)',
            },
          }}
        >
          Refresh Without Saving
        </Button>
        
        <Button
          onClick={handleSaveAndRefresh}
          variant="contained"
          startIcon={saving ? <CircularProgress size={16} color="inherit" /> : <SaveIcon />}
          disabled={saving}
          sx={{
            bgcolor: '#4CAF50',
            '&:hover': {
              bgcolor: '#45a049',
            },
          }}
        >
          {saving ? 'Saving...' : 'Save & Refresh'}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default UpdateRequiredModal;
